import { IconButton } from "@wix/design-system";
import { Add, ArrowRight, Stop } from "@wix/wix-ui-icons-common";
import classNames from "classnames";
import React, { useContext } from "react";
import { RicosContext, UploadContext, useDevService } from "./../../ricos-context/index.js";
import { useWixInstanceService } from "../request-service-provider/wix-instance-context.js";
import { useEditorProps } from "../some-editor-props-context.js";
import { AttachmentBar } from "./attachment-bar/attachment-bar.js";
import { handleFileUpload } from "./handle-file-upload.js";
import classes from "./prompt-input-area-scss.js";
const PromptInputArea = React.forwardRef(
  ({
    busy,
    readonly,
    placeholder,
    maxLength,
    onChange,
    onSubmit,
    value,
    busyValue,
    onCancel,
    zIndex,
    dataHook,
    minHeight,
    forceMultiline: forceMultilineProp,
    _attachments,
    className,
    style,
    allowFileUpload,
    presetBarComponent,
    actionBarComponent
  }, ref) => {
    const { t } = useContext(RicosContext);
    const { uploadService } = useContext(UploadContext);
    const devService = useDevService();
    const wixInstance = useWixInstanceService();
    const editorProps = useEditorProps();
    const inputRef = React.useRef(null);
    const hiddenInputRef = React.useRef(null);
    const [isMultiline, setIsMultiline] = React.useState(false);
    const [attachments, setAttachments] = React.useState(
      _attachments && _attachments?.length ? _attachments : []
    );
    React.useImperativeHandle(
      ref,
      () => {
        function focus(retries = 100) {
          setTimeout(() => {
            inputRef.current?.focus();
            if (document.activeElement !== inputRef.current && retries > 0) {
              focus(retries - 1);
            }
          }, 10);
        }
        return { focus };
      },
      []
    );
    const showPresetBar = Boolean(presetBarComponent);
    const showAttachments = allowFileUpload && attachments && attachments.length > 0;
    const forceMultiline = forceMultilineProp || showPresetBar || showAttachments || false;
    React.useLayoutEffect(() => {
      if (inputRef.current && hiddenInputRef.current) {
        const newHeight = Math.min(inputRef.current.scrollHeight, 216);
        inputRef.current.style.height = `${newHeight}px`;
        const SINGLE_LINE_HEIGHT_THRESHOLD = 25;
        const hasNewlines = value.includes("\n");
        const exceedsHeight = hiddenInputRef.current.scrollHeight > SINGLE_LINE_HEIGHT_THRESHOLD;
        const shouldBeMultiline = forceMultiline || hasNewlines || exceedsHeight;
        if (forceMultiline) {
          setIsMultiline(true);
          const height = minHeight || "66px";
          inputRef.current.style.height = height;
          inputRef.current.style.minHeight = height;
        } else if (value === "" || !exceedsHeight) {
          setIsMultiline(false);
          inputRef.current.style.height = "24px";
          inputRef.current.style.minHeight = "";
        } else if (shouldBeMultiline) {
          setIsMultiline(true);
          if (minHeight) {
            inputRef.current.style.minHeight = minHeight;
          }
        }
      }
    }, [value, forceMultiline, minHeight]);
    return /* @__PURE__ */ React.createElement(
      "div",
      {
        className: classNames(
          classes.root,
          {
            [classes.multiline]: isMultiline,
            [classes.forceMultiline]: forceMultiline
          },
          className
        ),
        style
      },
      (showPresetBar || showAttachments) && /* @__PURE__ */ React.createElement("div", { className: classes.topBar }, presetBarComponent, showAttachments ? /* @__PURE__ */ React.createElement(
        AttachmentBar,
        {
          attachments,
          onRemove: (id) => setAttachments(attachments.filter((attachment) => attachment.id !== id))
        }
      ) : null),
      /* @__PURE__ */ React.createElement(
        "textarea",
        {
          ref: hiddenInputRef,
          className: classNames(classes.textInput, classes.hiddenInput, {
            [classes.forceMultiline]: forceMultiline,
            [classes.allowFileUpload]: allowFileUpload
          }),
          value: busy ? busyValue ?? t("AIPlugin_Input_Loading_Text") : value,
          readOnly: true,
          "aria-hidden": true
        }
      ),
      /* @__PURE__ */ React.createElement(
        "textarea",
        {
          ref: inputRef,
          "data-hook": dataHook,
          name: t(placeholder ?? "AIPlugin_Input_Placeholder_Text"),
          className: classNames(classes.textInput, {
            [classes.hasTopBar]: showPresetBar || showAttachments,
            [classes.forceMultiline]: forceMultiline,
            [classes.allowFileUpload]: allowFileUpload
          }),
          placeholder: t(placeholder ?? "AIPlugin_Input_Placeholder_Text"),
          value: busy ? busyValue ?? t("AIPlugin_Input_Loading_Text") : value,
          onChange: (e) => onChange(e.target.value),
          maxLength,
          readOnly: readonly,
          disabled: busy,
          onKeyDown: (e) => {
            if (e.key === "Escape") {
              if (busy) {
                e.stopPropagation();
                onCancel?.();
                return;
              }
            }
            if (!e.shiftKey && e.key === "Enter") {
              if (!value) {
                e.stopPropagation();
                e.preventDefault();
                return;
              }
              if (e.key === "Enter" && !busy && value) {
                onSubmit({ text: value, attachments });
                return;
              }
            }
            if (["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key)) {
              e.stopPropagation();
            }
          }
        }
      ),
      /* @__PURE__ */ React.createElement(
        "div",
        {
          className: classNames(classes.actionsContainer, {
            [classes.forceMultiline]: forceMultiline,
            [classes.allowFileUpload]: allowFileUpload
          })
        },
        (allowFileUpload || actionBarComponent) && /* @__PURE__ */ React.createElement(
          "div",
          {
            style: {
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              pointerEvents: "auto"
            }
          },
          allowFileUpload && /* @__PURE__ */ React.createElement(
            IconButton,
            {
              onClick: async () => {
                await handleFileUpload({
                  services: {
                    uploadService,
                    devService,
                    wixInstance,
                    editorProps
                  }
                });
              },
              priority: "tertiary",
              skin: "dark"
            },
            /* @__PURE__ */ React.createElement(Add, null)
          ),
          actionBarComponent
        ),
        /* @__PURE__ */ React.createElement("div", { className: classes.trailing }, busy ? /* @__PURE__ */ React.createElement(IconButton, { onClick: onCancel, priority: "secondary" }, /* @__PURE__ */ React.createElement(Stop, null)) : /* @__PURE__ */ React.createElement(IconButton, { onClick: () => onSubmit({ text: value, attachments }), disabled: !value }, /* @__PURE__ */ React.createElement(ArrowRight, null)))
      )
    );
  }
);
export {
  PromptInputArea
};
//# sourceMappingURL=prompt-input-area.js.map
